// =============================================================================
// DiagBanner - Visual indicator for active session diagnostic replay
// =============================================================================
// Renders a fixed red banner at the top of the viewport when a diagnostic
// replay session is active. Cannot be dismissed — always visible during replay.
// Shows target user info, remaining time, and an end button.
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';

export function DiagBanner() {
  const { _diag } = useAuthStore();

  // Compute remaining minutes from _exp timestamp
  const computeRemaining = useCallback(() => {
    if (!_diag?._exp) return 0;
    return Math.max(0, Math.round((new Date(_diag._exp).getTime() - Date.now()) / 60_000));
  }, [_diag?._exp]);

  const [remaining, setRemaining] = useState(computeRemaining);

  // Update countdown every 30 seconds, anchored to _exp
  useEffect(() => {
    if (!_diag?.active) return;

    setRemaining(computeRemaining());

    const interval = setInterval(() => {
      setRemaining(computeRemaining());
    }, 30_000);

    return () => clearInterval(interval);
  }, [_diag?.active, computeRemaining]);

  if (!_diag?.active) return null;

  const handleEnd = async () => {
    try {
      await api.post('/internal/diag/restore');
    } catch {
      // Restore may fail if session expired — reload either way
    }
    window.location.href = '/';
  };

  const displayName = _diag._refName || 'Unknown';
  const displayEmail = _diag._refEmail ? ` (${_diag._refEmail})` : '';

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] bg-red-600 text-white px-4 py-2 text-sm font-medium flex items-center justify-center gap-3"
      style={{ minHeight: '40px' }}
    >
      <span className="inline-block w-2 h-2 rounded-full bg-white animate-pulse" />
      <span>
        DEBUG SESSION: {displayName}{displayEmail}
        {' | '}
        {remaining} min remaining
      </span>
      <button
        onClick={handleEnd}
        className="ml-3 px-3 py-0.5 bg-white text-red-600 rounded text-xs font-bold hover:bg-red-50 transition-colors"
      >
        End
      </button>
    </div>
  );
}
